import {
  getClients,
  getClientAnalytics,
  getMessagesTo,
  getPropertyDistrictsByIds,
  getStaffProfile,
  getUserProfile,
  patchClient,
  patchClientFlowStatus,
  patchClientProfile,
  patchStaffProfile,
  rawGetRequest,
} from "@/services/brokerService";
import Vue from "vue";
import {loadPropertyDistricts} from "@/utils/clientDistricts";

const deepEqual = require('deep-equal');

async function enrichEmployeesWithStaffProfile(employees) {
  return Promise.all(employees.map(async (employee) => {
    const staff_profile = (await getStaffProfile(employee.id)).staff_profile;
    return {...employee, staff_profile};
  }));
}

const defaultFilters = {
  expand: "client_profile,mortgages,incomes",
  page_size: 10,
  ordering: "-date_joined",
  is_applicant_or_prospect: true
}

const defaultClientsConfig = {
    new: {
      broker_stage: "new",
      ...defaultFilters
    },
    missed: {
      broker_stage: "missed",
      ...defaultFilters
    },
    approval: {
      broker_stage: "approval",
      ...defaultFilters
    },
    waiting: {
      broker_stage: "waiting",
      ...defaultFilters
    },
    archived: {
      broker_stage: "archived",
      ...defaultFilters
    },
}

export default {
  namespaced: true,
  state: {
    employees: {
      count: 0,
      results: [],
    },
    clientsConfig: structuredClone(defaultClientsConfig),
    clientsHistory: {
      config: {},
      counts: {
        new: 0,
        missed: 0,
        approval: 0,
        waiting: 0,
        archived: 0
      }
    },
    clientAnalytics: {
      current: null,
      previous: null,
      filters: {
        current: {is_applicant_or_prospect: true},
        previous: null,
      },
      loading: false,
      error: null,
    },
    clients: {},
    propertyDistricts: {},
    clientUnseenMessages: [],
    clientUnseenMessagesAmount: 0,

    clientsLoading: false,
    clientsHistoryLoading: false
  },
  getters: {
    getClientsConfigs: (state) => {
      return state.clientsConfig;
    },
    getClientsByType: (state) => (type) => {
      if (state.clients.hasOwnProperty(type)) {
        return state.clients[type].clients;
      } else {
        return [];
      }
    },
    getClientsNextByType: (state) => (type) => {
      if (state.clients.hasOwnProperty(type)) {
        return state.clients[type].next;
      } else {
        return "";
      }
    },
    getCountOfClientsByType: (state) => (type) => {
      if (state.clients.hasOwnProperty(type)) {
        return state.clients[type].count;
      } else {
        return 0;
      }
    },
    getTypeByClientProfile: (state, getters) => (profile) => {
      if (profile.ob_status === "rozpracovaný") {
        const flowKey = profile.client_flow_status?.flow?.key;
        return getters["getTypeByClientFlow"](flowKey);
      } else {
        return getters["getTypeByObStatus"](profile.ob_status);
      }
    },
    getTypeByClientFlow: (state) => (flow) => {
      if (["registrovany", "not_contacted"].includes(flow)) return "new";
      if (["contact_not_successful", "contact_not_successful_2", "contact_not_successful_3"].includes(flow)) return "missed";
      return "approval";
    },
    getTypeByObStatus: (state) => (status) => {
      return {novy: "new", cakacka: "waiting", archivovany: "archived"}[status];
    },
    getTypeByClientId: (state) => (id) => {
      for (const [key, clientsObject] of Object.entries(state.clients)) {
        const index = clientsObject.clients.findIndex((client) => client.id === id);
        if (index !== -1) return key;
      }

      return undefined;
    },
    getClientsHistoryConfig: (state) => {
      const out = {};

      const base = state.clientsConfig;
      const historyConfig = state.clientsHistory.config;

      for (const type of Object.keys(base)) {
        out[type] = {
          ...base[type],
          ...historyConfig
        };
      }

      return out;
    },
    getClientHistoryCount: (state) => {
      return state.clientsHistory.counts;
    },

    getClientsLoading: (state) => {
      return state.clientsLoading;
    },
    getClientsHistoryLoading: (state) => {
      return state.clientsHistoryLoading;
    },
    getClientAnalytics: (state) => state.clientAnalytics.current,
    getPreviousClientAnalytics: (state) => state.clientAnalytics.previous,
    getClientAnalyticsLoading: (state) => state.clientAnalytics.loading,
    getClientAnalyticsError: (state) => state.clientAnalytics.error,

    getEmployeeById: (state) => (id) => {
      const index = state.employees.results.map(e => e.id).indexOf(id);
      if (index === -1) return null;
      return state.employees.results[index];
    },
    getFinancnici: (state) => {
      return state.employees.results.filter(
        (e) => e.role === "financnik"
      );
    },
    getAdmini: (state) => {
      return state.employees.results.filter((e) => e.role === "alex_admin");
    },
    getExperts: (state) => {
      return state.employees.results.filter((e) => e.role === "znalec");
    },

    totalAmountOfUnseenMessages: (state) => {
      let totalAmount = 0;
      state.clientUnseenMessages.forEach(
        (item) => (totalAmount += item.amount_unseen)
      );
      return totalAmount;
    },
    getUnseenMessages: (state) => {
      return state.clientUnseenMessages.sort((a, b) => {
        return (
          new Date(b.created) - new Date(a.created)
        );
      });
    },
  },
  mutations: {
    setPropertyDistricts(state, districts) {
      Vue.set(state, "propertyDistricts", {...state.propertyDistricts, ...districts});
    },
    setClientsByType(state, {type, clients, next, count}) {
      Vue.set(state.clients, type, {clients, next, count});
    },
    appendClientsByType(state, {type, clients, next, count}) {
      if (state.clients.hasOwnProperty(type)) {
        state.clients[type].next = next;
        state.clients[type].count = count;
        state.clients[type].clients.push(...clients);
      }
    },
    // setClientsHistoryCountsByType(state, {type, count}) {
    //   Vue.set(state.clientsHistoryCount, type, count);
    // },
    setClientsHistoryConfig(state, config) {
      Vue.set(state.clientsHistory, "config", {...config, page_size: 1});
    },
    setClientsHistoryCount(state, {type, count}) {
      Vue.set(state.clientsHistory.counts, type, count);
    },
    removeClientsByType(state, type) {
      Vue.delete(state.clientsConfig, type);
      Vue.delete(state.clients, type);
    },
    registerClientConfigType(state, {type, config}) {
      const filters = {...defaultFilters, ...config};
      // Search is a contact lookup across all lifecycle stages, including
      // coapplicants and serviced clients who have their own detail card.
      if (type === "search") delete filters.is_applicant_or_prospect;
      Vue.set(state.clientsConfig, type, filters);
    },
    patchClientObject(state, {client, type}) {
      if (!state.clients.hasOwnProperty(type))
        return;

      delete client.client_profile;
      delete client.incomes;
      delete client.debts;
      delete client.mortgages;

      const clientObject = state.clients[type].clients.find((c) => c.id === client.id);
      if (clientObject) {
        Object.assign(clientObject, client)
      }
    },
    patchClientProfileObject(state, {clientProfile, type}) {
      if (!state.clients.hasOwnProperty(type))
        return;

      const clientId = clientProfile.user;
      const client = state.clients[type].clients.find((client) => client.id === clientId);
      if (client) {
        Object.assign(client.client_profile, clientProfile)
      }
    },
    transferClientBetweenTypes(state, {clientId, from, to}) {
      if (from === to)
        return;

      if (!state.clients.hasOwnProperty(from))
        return;

      const currentIndex = state.clients[from].clients.findIndex((c) => c.id === clientId);

      if (currentIndex === -1) return null;

      const transferredClient = state.clients[from].clients.splice(currentIndex, 1)[0];
      state.clients[from].count -= 1;

      if (!to)
        return

      if (state.clients.hasOwnProperty(to)) {
        state.clients[to].clients.push(transferredClient);
        state.clients[to].clients.sort((a, b) => b.id - a.id);
      } else {
        Vue.set(state.clients, to, {clients: [transferredClient], next: null});
      }

      state.clients[to].count += 1;
    },
    updateAllClientsConfigs(state, config) {
      for (const key of Object.keys(state.clientsConfig)) {
        Object.assign(state.clientsConfig[key], config);
      }
    },
    resetClientsConfig(state) {
      Vue.set(state, "clientsConfig", structuredClone(defaultClientsConfig));
    },
    deleteAllClients(state) {
      Vue.set(state, "clients", {});
    },

    setClientLoading(state, loading) {
      state.clientsLoading = loading;
    },
    setClientHistoryLoading(state, loading) {
      state.clientsHistoryLoading = loading;
    },
    setClientAnalyticsFilters(state, filters) {
      Vue.set(state.clientAnalytics, "filters", {
        current: {is_applicant_or_prospect: true, ...(filters.current || {})},
        previous: filters.previous
          ? {is_applicant_or_prospect: true, ...filters.previous}
          : null,
      });
    },
    resetClientAnalyticsFilters(state) {
      Vue.set(state.clientAnalytics, "filters", {
        current: {is_applicant_or_prospect: true},
        previous: null,
      });
    },
    setClientAnalyticsData(state, {current, previous}) {
      Vue.set(state.clientAnalytics, "current", current);
      Vue.set(state.clientAnalytics, "previous", previous);
    },
    setClientAnalyticsLoading(state, loading) {
      Vue.set(state.clientAnalytics, "loading", loading);
    },
    setClientAnalyticsError(state, error) {
      Vue.set(state.clientAnalytics, "error", error);
    },

    nullState (state) {
      state.clients = {}
      state.propertyDistricts = {}
      state.clientUnseenMessages = []
      state.clientUnseenMessagesAmount = 0
    },
    registerClients (state, data) {
      const clientsObject = {
        results: data.retrievedClients,
        count: data.count,
      };
      Vue.set(state, data.typeOfArray, clientsObject);
    },
    updateEmployees: (state, data) => {
      let filteredEmployees = state.employees.results.filter((e) => !data.roles.includes(e.role));
      filteredEmployees = [...filteredEmployees, ...data.employees];
      state.employees = {
        results: filteredEmployees,
        count: filteredEmployees.length,
      };
    },
    updateEmployeeStaffProfile: (state, staffProfile) => {
      const employee = state.employees.results.find((e) => e.id === staffProfile.user);
      if (!employee)
        return;

      Object.assign(employee.staff_profile, staffProfile);
    },
    updateChatOfExistingClient (state, messages) {
      state.clientUnseenMessages = messages;
    },
  },
  actions: {
    fetchAllClientsFromConfig: async ({dispatch, commit, rootGetters}) => {
      commit("setClientLoading", true);
      commit("deleteAllClients");

      const clientConfigs = rootGetters["clientState/getClientsConfigs"];
      const clientsResponses = await dispatch("fetchClientResponses", clientConfigs);

      for (const [key, response] of Object.entries(clientsResponses)) {
        const clientObj = {
          type: key,
          clients: response.results,
          count: response.count,
          next: response.next
        }
        commit("setClientsByType", clientObj);
      }

      commit("setClientLoading", false);
    },
    fetchClientsNextPageByType: async ({dispatch, commit, rootGetters}, type) => {
      let clientNextPage = rootGetters["clientState/getClientsNextByType"](type);

      clientNextPage = new URL(clientNextPage);
      clientNextPage = clientNextPage.pathname.replace(/^\/api/, '') + clientNextPage.search;

      const response = await rawGetRequest(clientNextPage);
      await dispatch("fetchClientPropertyDistricts", response.results);

      const clientObj = {
        type,
        clients: response.results,
        count: response.count,
        next: response.next
      }

      commit("appendClientsByType", clientObj);
    },
    fetchClientHistoryCounts: async ({dispatch, commit, rootGetters}) => {
      commit("setClientHistoryLoading", true);

      const clientHistoryConfigs = rootGetters["clientState/getClientsHistoryConfig"];
      const clientsResponses = await dispatch("fetchClientResponses", clientHistoryConfigs);

      for (const [key, response] of Object.entries(clientsResponses)) {
        const clientObj = {
          type: key,
          count: response.count,
        }
        commit("setClientsHistoryCount", clientObj);
      }

      commit("setClientHistoryLoading", false);
    },
    fetchClientAnalytics: async ({state, commit}) => {
      commit("setClientAnalyticsLoading", true);
      commit("setClientAnalyticsError", null);

      try {
        const currentRequest = getClientAnalytics(state.clientAnalytics.filters.current);
        const previousRequest = state.clientAnalytics.filters.previous
          ? getClientAnalytics(state.clientAnalytics.filters.previous)
          : Promise.resolve(null);
        const [current, previous] = await Promise.all([currentRequest, previousRequest]);
        commit("setClientAnalyticsData", {current, previous});
        return current;
      } catch (error) {
        commit("setClientAnalyticsError", "Štatistiky sa nepodarilo načítať.");
        return null;
      } finally {
        commit("setClientAnalyticsLoading", false);
      }
    },
    refreshClientsData: async ({dispatch, commit, rootGetters}) => {
      const clientConfigs = structuredClone(rootGetters["clientState/getClientsConfigs"]);

      for (const key of Object.keys(clientConfigs)) {
        clientConfigs[key].page_size = rootGetters["clientState/getClientsByType"](key).length;
        clientConfigs[key].page = 1;
      }

      const clientsResponses = await dispatch("fetchClientResponses", clientConfigs);

      for (const [key, response] of Object.entries(clientsResponses)) {
        const next = rootGetters["clientState/getClientsNextByType"](key);
        const clientObj = {
          type: key,
          clients: response.results,
          count: response.count,
          next: next
        }
        commit("setClientsByType", clientObj);
      }
    },
    fetchClientPropertyDistricts: async ({commit}, clients) => {
      const districts = await loadPropertyDistricts(clients, getPropertyDistrictsByIds);
      commit("setPropertyDistricts", districts);
    },
    fetchClientResponses: async ({dispatch}, clientsConfig) => {
      const responses = Object.fromEntries(
        await Promise.all(
          Object.entries(clientsConfig).map(async ([key, config]) => {
            return [key, await getClients(config)]
          })
        )
      );
      await dispatch("fetchClientPropertyDistricts", Object.values(responses).flatMap(response => response.results));
      return responses;
    },
    archiveClient: async ({dispatch, commit}, {type, clientProfileId, reason}) => {
      commit("setClientLoading", true);

      const clientProfileObject = {
        ob_status: "archivovany",
        archiv_resaon: reason
      };
      const patchedProfile = await dispatch("patchClientProfile", {
        profile: clientProfileObject,
        id: clientProfileId,
        type
      });

      const to = "archived";
      commit("transferClientBetweenTypes", {
        clientId: patchedProfile.user,
        from: type,
        to,
      });

      commit("setClientLoading", false);

      return patchedProfile;
    },
    // unarchiveClient:  async ({dispatch, commit, rootGetters}, {clientId}) => {
    //   commit("appState/setLoadingPage", true, {root: true});
    //   const clientToUnarchive = rootGetters["clientState/getClientByTypeWithId"]("archived", clientId);
    //
    //   const clientProfile = clientToUnarchive.client_profile;
    //   const clientFlowStatus = clientProfile.client_flow_status;
    //
    //   const clientProfileObject = { ob_status: null };
    //   let to = null;
    //   const from = "archived"
    //
    //   if (clientFlowStatus) {
    //     clientProfileObject.ob_status = "rozpracovaný";
    //     to = rootGetters["clientState/getTypeByClientFlow"](clientFlowStatus.flow.key);
    //   } else {
    //     clientProfileObject.ob_status = "novy";
    //   }
    //
    //   const patchedProfile = await dispatch("patchClientProfile", {
    //     profile: clientProfileObject,
    //     id: clientProfile.id,
    //     type: from
    //   });
    //
    //   commit("transferClientBetweenTypes", {
    //     clientId: patchedProfile.user,
    //     from,
    //     to
    //   });
    //   commit("appState/setLoadingPage", false, {root: true});
    // },
    patchClientObStatus: async({dispatch, commit, rootGetters}, {id, status, type, currentProfile}) => {
      const profileObject = { ob_status: status };

      if (type === "archived")
        profileObject.archiv_resaon = null;

      const expectedProfile = {...currentProfile};
      expectedProfile.ob_status = status;

      const newType = rootGetters["clientState/getTypeByClientProfile"](expectedProfile);

      commit("patchClientProfileObject", {clientProfile: expectedProfile, type});
      commit("transferClientBetweenTypes", {
        clientId: expectedProfile.user,
        from: type,
        to: newType,
      });

      try {
        return await dispatch("patchClientProfile", {
          profile: profileObject,
          id,
          type: newType
        });
      } catch (e) {
        commit("patchClientProfileObject", {clientProfile: currentProfile, type: newType});
        commit("transferClientBetweenTypes", {
          clientId: expectedProfile.user,
          from: newType,
          to: type,
        });
        commit("appState/setSuccessMessage", {
          text: `Nepodarilo sa zmeniť klientovi status`,
          type: "error"
        }, {root: true});

        return null;
      }
    },
    patchClientFlowState: async ({commit, rootGetters}, { profile, type, flow }) => {
      let newType = rootGetters["clientState/getTypeByClientFlow"](flow.key) || type;
      if (["waiting", "archived"].includes(type))
        newType = type;

      commit("transferClientBetweenTypes", {
        clientId: profile.user,
        from: type,
        to: newType,
      })
      try {
        const patchedProfile = await patchClientFlowStatus(flow, profile.id);
        commit("patchClientProfileObject", {clientProfile: patchedProfile, type: newType});
        return patchedProfile;
      } catch (e) {
        commit("transferClientBetweenTypes", {
          clientId: profile.user,
          from: newType,
          to: type,
        })
        commit("appState/setSuccessMessage", {
            text: `Nepodarilo sa uložiť stav klienta`,
            type: "error"
          }, { root: true }
        );
        return null;
      }
    },

    patchClientProfile: async({commit}, {profile, id, type}) => {
      const patchedProfile = await patchClientProfile(profile, id);
      commit("patchClientProfileObject", {clientProfile: patchedProfile, type});
      return patchedProfile;
    },
    patchClient: async({commit}, {client, id, type}) => {
      const patchedClient = await patchClient(id, client);
      commit("patchClientObject", {client: patchedClient, type});
      return patchedClient;
    },

    retrieveEmployees: async({commit, rootGetters}, employeesToGet) => {
      const employees = await getUserProfile({role__in: employeesToGet.toString(), page_size: 999});

      if (rootGetters["userState/getRole"] === "alex_admin")
        employees.results = await enrichEmployeesWithStaffProfile(employees.results);

      const employeesObject = {
        typeOfArray: "employees",
        retrievedClients: employees.results,
        count: employees.count,
      };
      commit("registerClients", employeesObject);
    },
    updateEmployees: async({commit, rootGetters}, employeesToGet) => {
      const employees = await getUserProfile({role__in: employeesToGet.toString(), page_size: 999});
      if (rootGetters["userState/getRole"] === "alex_admin")
        employees.results = await enrichEmployeesWithStaffProfile(employees.results);

      const employeesObject = {
        roles: employeesToGet,
        employees: employees.results,
      };
      commit("updateEmployees", employeesObject);
    },
    patchStaffProfile: async({commit}, {profile}) => {
      try {
        const patchedEmployeeProfile = await patchStaffProfile(profile.id, profile);
        commit("updateEmployeeStaffProfile", patchedEmployeeProfile);
        return patchedEmployeeProfile;
      } catch (e) {
        console.error(e);
        throw e;
      }
    },

    updateUnseenMessages: async ({commit, state}, clientId) => {
      const messages = await getMessagesTo(clientId, true);
      let clientUnseenMessages = [];
      messages.results.forEach((message) => {
        if (message.sender !== null) {
          const lastMessageFromClient = clientUnseenMessages.find(
            (lastMessage) => lastMessage.sender === message.sender
          );
          const indexOfClient = clientUnseenMessages.indexOf(lastMessageFromClient);
          const messageObj = clientUnseenMessages[indexOfClient];
          if (indexOfClient === -1) {
            clientUnseenMessages[clientUnseenMessages.length] = {
              ...message,
              amount_unseen: message.seen_at ? 0 : 1
            }
          } else {
            if (!message.seen_at && messageObj.id < message.id) {
              messageObj.message = message;
              messageObj.amount_unseen = messageObj.amount_unseen + 1;
            } else if (!message.seen_at && messageObj.id > message.id) {
              messageObj.amount_unseen = messageObj.amount_unseen + 1;
            }
          }
        }
      });
      if (deepEqual(state.clientUnseenMessages, clientUnseenMessages)) return;
      commit("updateChatOfExistingClient", clientUnseenMessages);
    },
  },
};
